import type { Household, HouseholdInvitation, HouseholdRole } from "../types/household"
import { apiRequest, subscribeViaApi, type Unsubscribe } from "./apiClient"

/**
 * Household management via the Edge Functions API.
 * Realtime change signals refetch the lists; membership changes are infrequent
 * enough that a refetch on every change is plenty.
 */
class HouseholdService {
  async createHousehold(
    userId: string,
    name: string,
    userEmail: string,
    userDisplayName: string | null,
    userPhotoURL: string | null,
    description?: string,
  ): Promise<Household> {
    void userId
    return apiRequest<Household>("/api/households", {
      method: "POST",
      body: { name, description, email: userEmail, displayName: userDisplayName, photoURL: userPhotoURL },
    })
  }

  getHousehold(householdId: string): Promise<Household | null> {
    return apiRequest<Household>(`/api/households/${householdId}`).catch(() => null)
  }

  async updateHousehold(
    householdId: string,
    // description and imageId accept null so clearing them persists —
    // undefined keys are dropped from the PATCH JSON and the server keeps the
    // old value.
    updates: Partial<Pick<Household, "name" | "settings">> & {
      description?: string | null
      imageId?: string | null
    },
  ): Promise<void> {
    await apiRequest<Household>(`/api/households/${householdId}`, { method: "PATCH", body: updates })
  }

  async deleteHousehold(householdId: string): Promise<void> {
    await apiRequest<void>(`/api/households/${householdId}`, { method: "DELETE" })
  }

  async removeMember(householdId: string, userId: string): Promise<void> {
    await apiRequest<void>(`/api/households/${householdId}/members/${userId}`, { method: "DELETE" })
  }

  async updateMemberRole(householdId: string, userId: string, newRole: HouseholdRole): Promise<void> {
    await apiRequest<void>(`/api/households/${householdId}/members/${userId}`, {
      method: "PATCH",
      body: { role: newRole },
    })
  }

  getUserInvitations(email: string): Promise<HouseholdInvitation[]> {
    void email
    return apiRequest<HouseholdInvitation[]>("/api/invitations/mine")
  }

  async acceptInvitation(
    invitationId: string,
    userId: string,
    userEmail: string | null,
    userDisplayName: string | null,
    userPhotoURL: string | null,
  ): Promise<void> {
    void userId
    void userEmail
    void userDisplayName
    void userPhotoURL
    await apiRequest<{ success: boolean }>(`/api/invitations/${invitationId}/accept`, {
      method: "POST",
      body: {},
    })
  }

  async declineInvitation(invitationId: string): Promise<void> {
    await apiRequest<void>(`/api/invitations/${invitationId}/decline`, { method: "POST" })
  }

  async deleteInvitation(invitationId: string): Promise<void> {
    await apiRequest<void>(`/api/invitations/${invitationId}`, { method: "DELETE" })
  }

  getUserHouseholds(userId: string): Promise<Household[]> {
    void userId
    return apiRequest<Household[]>("/api/households")
  }

  subscribeToHousehold(householdId: string, callback: (household: Household | null) => void): Unsubscribe {
    return subscribeViaApi<Household>(
      "household_members",
      `/api/households/${householdId}`,
      (h) => callback(h ?? null),
      { filter: `household_id=eq.${householdId}` },
    )
  }

  subscribeToUserHouseholds(userId: string, callback: (households: Household[]) => void): Unsubscribe {
    return subscribeViaApi<Household[]>("household_members", "/api/households", callback, {
      filter: `user_id=eq.${userId}`,
    })
  }

  subscribeToHouseholdInvitations(
    householdId: string,
    callback: (invitations: HouseholdInvitation[]) => void,
  ): Unsubscribe {
    return subscribeViaApi<HouseholdInvitation[]>(
      "household_invitations",
      `/api/households/${householdId}/invitations`,
      callback,
      { filter: `household_id=eq.${householdId}` },
    )
  }

  subscribeToUserInvitations(email: string, callback: (invitations: HouseholdInvitation[]) => void): Unsubscribe {
    void email
    return subscribeViaApi<HouseholdInvitation[]>("household_invitations", "/api/invitations/mine", callback)
  }
}

export const householdService = new HouseholdService()
export default householdService
